import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { HeroAvatar, useHeroLookup } from "@/features/matches";
import { cn } from "@/shared/lib";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/toggle-group";

import { isClipReady } from "../../model/clipState";
import type { HighlightsMatch } from "../../model/Highlight";
import type { HighlightsSearch } from "../../model/highlightsSearchSchema";

const SHOW_LABEL = msg`Which moments to show`;
const HERO_LABEL = msg`Hero`;
const ALL = msg`All`;
const READY = msg`Ready clips`;
const TODO = msg`No clip yet`;

type Filters = Pick<HighlightsSearch, "show" | "hero">;

type HighlightsFiltersProps = {
  // Матчи раздела без фильтра (безнадёжные моменты уже убраны): из них числа и герои.
  matches: HighlightsMatch[];
  filters: Filters;
  onChange: (filters: Filters) => void;
};

const ITEM = cn("min-h-10 px-3.5 text-sm");

// Фильтр раздела (доска 18д): все моменты, только готовые клипы или только без клипа и герой.
// Героев выбора — только если играл больше чем одним.
export const HighlightsFilters = ({ matches, filters, onChange }: HighlightsFiltersProps) => {
  const { i18n } = useLingui();
  const heroLookup = useHeroLookup();
  // Числа на кнопках — под выбранного героя: столько плиток и покажет фильтр.
  const items = matches
    .filter((match) => filters.hero === undefined || match.hero_id === filters.hero)
    .flatMap((match) => match.items);
  const ready = items.filter(isClipReady).length;
  const heroes = [...new Set(matches.map((match) => match.hero_id))];
  const kinds = [
    { value: "all", label: ALL, count: items.length },
    { value: "ready", label: READY, count: ready },
    { value: "todo", label: TODO, count: items.length - ready },
  ] as const;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToggleGroup
        type="single"
        variant="pill"
        aria-label={i18n._(SHOW_LABEL)}
        value={filters.show ?? "all"}
        onValueChange={(value) => {
          if (value === "all" || value === "") {
            onChange({ ...filters, show: undefined });
          } else if (value === "ready" || value === "todo") {
            onChange({ ...filters, show: value });
          }
        }}
        className="gap-2"
      >
        {kinds.map((kind) => (
          <ToggleGroupItem key={kind.value} value={kind.value} className={ITEM}>
            {i18n._(kind.label)}
            <span className="font-mono text-xs opacity-75">{kind.count}</span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {heroes.length > 1 && (
        <>
          <span aria-hidden="true" className="mx-1 h-6 w-px bg-border" />
          <ToggleGroup
            type="single"
            variant="pill"
            aria-label={i18n._(HERO_LABEL)}
            value={filters.hero === undefined ? "" : String(filters.hero)}
            onValueChange={(value) => {
              onChange({ ...filters, hero: value === "" ? undefined : Number(value) });
            }}
            className="gap-2"
          >
            {heroes.map((heroId) => {
              const hero = heroLookup(heroId);
              return (
                <ToggleGroupItem key={heroId} value={String(heroId)} className={cn(ITEM, "pl-1.5")}>
                  <HeroAvatar
                    name={hero.name}
                    iconUrl={hero.iconUrl}
                    size={26}
                    className="rounded-full"
                  />
                  {hero.name}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </>
      )}
    </div>
  );
};
